import React, { useState, useEffect } from 'react';
import { Button, Form, Upload, Modal, App, Input, Space } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import styles from './ProductDetailTemplate3.module.scss';
import type { UploadFile, UploadProps } from 'antd';
import { useAppSelector } from '@/store/hooks';
import TextArea from 'antd/es/input/TextArea';
import icon1 from '@/assets/images/templete3.jpg';
import { useTranslation } from 'react-i18next';
interface ImageUrl {
  url: string;
  name: string;
}

interface ContentItem {
  title: string;
  content: string;
}

export interface ProductDetailTemplate3Props {
  data: {
    name: string;
    bottomTitle: string;
    contentList: ContentItem[];
    imgUrl: ImageUrl[];
  };
  onChange?: (data: any, selfIndex: number) => void;
  editable?: boolean;
  selfIndex?: number;
  thumbnailMode?: boolean;//是否是缩略图模式
  onDelete?: (selfIndex: number) => void;
}

const ProductDetailTemplate3: React.FC<ProductDetailTemplate3Props> = ({
  data,
  onChange,
  editable = true,
  selfIndex = 0,
  thumbnailMode = false,
  onDelete
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation();
  // 预览状态
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  // 编辑表单数据
  const [bottomTitle, setBottomTitle] = useState(data?.bottomTitle || '');
  const [contentList, setContentList] = useState<ContentItem[]>(data?.contentList || []);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 获取token
  const token = useAppSelector((state) => state?.user.token);

  // 初始化数据
  useEffect(() => {
    if (data) {
      setBottomTitle(data.bottomTitle || '');
      setContentList(data.contentList || []);

      // 设置图片列表
      if (data.imgUrl && data.imgUrl.length > 0) {
        const images = data.imgUrl.map((item, index) => ({
          uid: `-${index}`,
          name: item.name || `image-${index}`,
          status: 'done' as const,
          url: item.url
        }));
        setFileList(images);
      }
    }
  }, [data]);

  // 图片上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`
    },
    listType: 'picture-card',
    fileList: fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
       message.error(t('showCase.Image10MB'));
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      // 复制一份文件列表
      const newFileList = [...info.fileList];

      // 对文件状态进行处理
      newFileList.forEach(file => {
        if (file.response) {
          file.url = file?.response?.data?.url;
        }
      });

      // 更新状态
      setFileList(newFileList);

      if (info.file.status === 'done') {
        message.success(`${info.file.name} ${t('showCase.uploadedsuccessfully')}`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} ${t('showCase.uploadfailed')}`);
      }
    },
    onPreview: async (file) => {
      let src = file.url as string;
      if (!src) {
        src = await new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as any);
          reader.onload = () => resolve(reader.result as string);
        });
      }
      // 使用 Modal 预览而不是 window.open 来避免跨域问题
      setPreviewImage(src);
      setPreviewVisible(true);
    }
  };

  // 打开编辑模式
  const handleEdit = () => {
    setIsEditing(true);
    form.setFieldsValue({
      bottomTitle,
      contentList: contentList.length > 0 ? contentList : [{ title: '', content: '' }]
    });
  };

  // 保存编辑
  const handleSave = () => {
    form.validateFields().then(values => {
      const updatedData = {
        ...data,
        bottomTitle: values.bottomTitle,
        contentList: values.contentList.filter((item: ContentItem) => item.title || item.content),
        imgUrl: fileList.map(file => ({
          url: file.url || '',
          name: file.name || ''
        }))
      };

      //debugger

      onChange && onChange(updatedData, selfIndex);
      setBottomTitle(values.bottomTitle);
      setContentList(values.contentList.filter((item: ContentItem) => item.title || item.content));
      setIsEditing(false);
      message.success(t('showCase.Templateupdatedsuccessfully'));
    });
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    // 恢复原始数据
    if (data) {
      setFileList(
        data.imgUrl.map((item, index) => ({
          uid: `-${index}`,
          name: item.name || `image-${index}`,
          status: 'done' as const,
          url: item.url
        }))
      );
    }
  };

  // 添加内容项
  const addContentItem = () => {
    const items = form.getFieldValue('contentList') || [];
    form.setFieldsValue({
      contentList: [...items, { title: '', content: '' }]
    });
  };

  // 删除内容项
  const removeContentItem = (index: number) => {
    const items = form.getFieldValue('contentList') || [];
    if (items.length <= 1) {
      message.warning(t('showCase.Atrequired'));
      return;
    }
    form.setFieldsValue({
      contentList: items.filter((_: any, i: number) => i !== index)
    });
  };

  // 上传按钮
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t('showCase.Upload')}</div>
    </div>
  );

  return (
    <div className={styles['template-container']}>
      {thumbnailMode ? (
        <div className={styles['template-thumbnail']} onClick={handleEdit}>
          <div className={styles['thumbnail-number']}>
            {selfIndex + 1}
          </div>
          <img className={styles['thumbnail-image']} src={icon1 || ''} alt="" />
          <div className={styles['thumbnail-actions']}>
            <Button 
              size="small"
              danger
              className={styles['delete-btn']}
              onClick={(e) => {
                e.stopPropagation();
                // 这里需要调用删除函数，但需要从父组件传入
                onDelete && onDelete(selfIndex);
              }}
            >
               {t('showCase.Delete')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles['template-content']}>
          <div className={styles['template-content-item']}>
            <div className={styles['image-container']}>
              {fileList.length > 0 && (
                <img
                  src={fileList[0].url}
                  alt={bottomTitle}
                  className={styles['template-image']}
                />
              )}
            </div>
            <div className={styles['details-container']}>
              {contentList.map((item, index) => (
                <div key={index} className={styles['detail-item']}>
                  <div className={styles['detail-title']}>{item.title}</div>
                  <div className={styles['detail-content']}>{item.content}</div>
                </div>
              ))}
            </div>
          </div>




          <div className={styles['bottom-title']}>{bottomTitle}</div>
        </div>
      )}

      

      {/* 编辑模式 */}
      {isEditing && (
        <Modal
          title={t('showCase.EditTemplate')}
          open={isEditing}
          onOk={handleSave}
          onCancel={handleCancel}
          okText={t('showCase.Save')}
          cancelText={t('membership.Cancel')}
          className={styles['edit-modal']}
          width={700}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              bottomTitle,
              contentList: contentList.length > 0 ? contentList : [{ title: '', content: '' }]
            }}
          >
            <Form.Item
              label={t('showCase.BottomTitle')}
              name="bottomTitle"
              rules={[{ required: true, message: t('showCase.Pleasebottomtitle') }]}
            >
              <Input placeholder={t('showCase.Pleasebottomtitle')} maxLength={50} />
            </Form.Item>

            <Form.List name="contentList">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className={styles['content-item']}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Form.Item
                          {...restField}
                          name={[name, 'title']}
                          label={t('showCase.Title')}
                          rules={[{ required: true, message: t('showCase.Pleaseentertitle') }]}
                        >
                          <Input placeholder={t('showCase.Pleaseentertitle')} maxLength={50} />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'content']}
                          label={t('showCase.Content')}
                          rules={[{ required: true, message: t('showCase.Pleaseentercontent') }]}
                        >
                          <TextArea placeholder={t('showCase.Pleaseentercontent')} rows={2} maxLength={100} />
                        </Form.Item>
                      </Space>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeContentItem(name)}
                          className={styles['delete-button']}
                        />
                      )}
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={addContentItem}
                      block
                      icon={<PlusOutlined />}
                    >
                      {t('showCase.AddContentItem')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item
              label="Image"
            >
              <Upload {...uploadProps}>
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>
              <div className={styles['upload-hint']}>
                {t('showCase.Please500')}
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* 图片预览 Modal */}
      <Modal
        open={previewVisible}
        title={t('showCase.ImagePreview')}
        footer={null}
        width={'80%'}
        onCancel={() => setPreviewVisible(false)}
        zIndex={2000} 
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default ProductDetailTemplate3; 